<template>
  <BottomSheet :open="open" back @update:open="$emit('update:open', $event)">
    <template #title>{{ t('receipt.pick.title') }}</template>
    <template #meta>{{ meta }}</template>

    <SearchField
      v-model="query"
      :label="t('receipt.pick.title')"
      :placeholder="t('item.search_placeholder')"
      clearable
    />

    <p v-if="phase === 'loading' && results.length === 0" class="note" role="status">
      {{ t('state.loading') }}
    </p>
    <p v-else-if="phase === 'offline'" class="note">{{ t('item.offline.body') }}</p>
    <p v-else-if="phase === 'error'" class="note">{{ t('error.internal') }}</p>

    <AppCard v-if="results.length > 0" class="results" :class="{ stale }" list>
      <button
        v-for="entry in results"
        :key="entry.id"
        class="result"
        type="button"
        @click="$emit('picked', { id: entry.id, name: entry.name })"
      >
        <span class="result-name">{{ entry.name }}</span>
        <span v-if="entry.note" class="result-note">{{ entry.note }}</span>
      </button>
    </AppCard>

    <!-- The line's own name stays possible: a new item is made by «Записать» itself (Р-1 of MOL-113). -->
    <AppCard v-if="newName && nameFits" class="keep" list>
      <button class="result keep-new" type="button" @click="$emit('picked', { name: newName })">
        <span class="result-name">{{ t('receipt.pick.keep_new', { text: newName }) }}</span>
        <span class="result-note">{{ t('receipt.pick.keep_new_note') }}</span>
      </button>
    </AppCard>
    <!-- A name the catalogue would refuse is said here, not by the receipt refused from the queue. -->
    <p v-else-if="newName" class="note">{{ t('receipt.pick.bad_name') }}</p>
    <p class="note">{{ t('receipt.pick.note') }}</p>
  </BottomSheet>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { drawsNothing, newItemSchema, pastedLine } from '@molvia/model'
import AppCard from '@/components/AppCard.vue'
import BottomSheet from '@/components/BottomSheet.vue'
import SearchField from '@/components/SearchField.vue'
import { useCatalogueSearch } from '@/composables/useCatalogueSearch'

/**
 * «Выбрать товар» (handoff 05, 5g): a sheet over the line's sheet — «‹», no × (Д-1) — with the
 * catalogue's own search, the query starting as the line's gloss, and last «Оставить новым товаром».
 * The item chosen goes back to the line; nothing is written until «Записать».
 */
export default defineComponent({
  name: 'ItemPickSheet',
  components: { AppCard, BottomSheet, SearchField },
  props: {
    open: { type: Boolean, required: true },
    /** The line as printed; empty for a line the person typed (MOL-273), which has none. */
    printed: { type: String, default: '' },
    translation: { type: String as PropType<string | null>, default: null },
    /** What the line is called now — the first query, and the name a new item would take. */
    start: { type: String, default: '' },
  },
  emits: {
    'update:open': (open: boolean) => typeof open === 'boolean',
    picked: (item: { id: string; name: string } | { name: string }) => typeof item === 'object',
  },
  setup(props) {
    const { t } = useI18n()
    const query = ref('')
    const { phase, results, stale } = useCatalogueSearch(query)
    watch(
      () => props.open,
      (open) => {
        if (open) query.value = props.translation ?? props.start
      },
      { immediate: true },
    )
    const newName = computed(() => {
      const value = pastedLine(query.value)
      return drawsNothing(value) ? null : value
    })
    return {
      t,
      query,
      phase,
      results,
      stale,
      newName,
      nameFits: computed(
        () => newName.value !== null && newItemSchema.shape.name.safeParse(newName.value).success,
      ),
      meta: computed(() =>
        props.printed === ''
          ? t('receipt.pick.meta_added')
          : props.translation
            ? t('receipt.pick.meta_translation', {
                printed: props.printed,
                text: props.translation,
              })
            : t('receipt.pick.meta', { printed: props.printed }),
      ),
    }
  },
})
</script>

<style scoped lang="scss">
.note {
  margin: var(--space-3) 0 0;
  color: var(--text-muted);
  font-size: var(--text-footnote);
}

.results,
.keep {
  margin-top: var(--space-3);
}

.stale {
  opacity: var(--opacity-stale);
}

/* Taken off in `:where()`, as `ListRow`'s: an equal selector took the list card's hairline between the
   results off (MOL-349, С-5). */
:where(.result) {
  border: 0;
}

.result {
  display: grid;
  gap: var(--space-1);
  width: 100%;
  min-height: var(--touch-target-lg);
  padding: var(--space-3) var(--space-4);
  color: var(--text);
  background: var(--surface);
  text-align: left;
  font: inherit;
  cursor: pointer;

  &:hover {
    background: var(--surface-2);
  }

  &:focus-visible {
    @include focus-ring;
  }
}

.result-name {
  font-weight: var(--weight-medium);
  overflow-wrap: anywhere;
}

.result-note {
  color: var(--text-muted);
  font-size: var(--text-footnote);
  overflow-wrap: anywhere;
}

.keep-new .result-name {
  color: var(--accent-ink);
}
</style>
